import { formatDistance } from 'date-fns'

import {
  ChartColumnIcon,
  CloudCheckIcon,
  MessageSquareIcon,
  Popover,
} from '@buffer-mono/popcorn'

import {
  SyncPopoverContent,
  SyncSection,
  SyncSectionText,
} from '~publish/components/SyncPopover'
import syncPopoverStyles from '~publish/components/SyncPopover/syncPopover.module.css'

import { useAggregateSyncStatus } from './useAggregateSyncStatus'

import styles from './SyncStatus.module.css'

import type { JSX } from 'react'

/** Metrics are collected on a daily cycle for every channel. */
const METRICS_CADENCE = 'Updated every 24 h'

function getTimeAgo(date: Date | null, now: Date = new Date()): string {
  if (!date) return 'recently'
  return formatDistance(date, now, { addSuffix: true })
}

export function SyncStatus(): JSX.Element | null {
  const {
    loading,
    postMetricsLastSyncedAt,
    channelMetricsLastSyncedAt,
    commentsLastSyncedAt,
    channelCount,
  } = useAggregateSyncStatus()

  if (loading || channelCount === 0) return null

  return (
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={styles.trigger}
          aria-label="About sync status"
        >
          <div className={styles.iconCircle}>
            <CloudCheckIcon className={styles.icon} />
          </div>
        </button>
      </Popover.Trigger>
      <Popover.Content
        aria-label="Sync status"
        className={syncPopoverStyles.content}
        align="end"
        side="bottom"
        arrow
      >
        <SyncPopoverContent title="How this page stays updated">
          <SyncSection
            icon={<ChartColumnIcon size="small" />}
            label="Post metrics"
            trailing={
              <SyncSectionText>
                {getTimeAgo(postMetricsLastSyncedAt)}
              </SyncSectionText>
            }
          >
            <SyncSectionText>{METRICS_CADENCE}</SyncSectionText>
          </SyncSection>
          <SyncSection
            icon={<ChartColumnIcon size="small" />}
            label="Channel metrics"
            trailing={
              <SyncSectionText>
                {getTimeAgo(channelMetricsLastSyncedAt)}
              </SyncSectionText>
            }
          >
            <SyncSectionText>{METRICS_CADENCE}</SyncSectionText>
          </SyncSection>
          <SyncSection
            icon={<MessageSquareIcon size="small" />}
            label="Comments"
            trailing={
              <SyncSectionText>
                {getTimeAgo(commentsLastSyncedAt)}
              </SyncSectionText>
            }
          >
            <SyncSectionText>Updated periodically</SyncSectionText>
          </SyncSection>
        </SyncPopoverContent>
      </Popover.Content>
    </Popover>
  )
}
